import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import * as Sentry from '@sentry/nextjs';
import {
  CancelledError,
  hashKey,
  type InfiniteData,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  browseSearchVariables,
  type MarketplaceBrowseListings,
  type MarketplaceBrowsePage,
  marketplaceBrowseQueryKey,
  marketplaceBrowseQueryOptions,
  type MarketplaceBrowseVariables,
  type MarketplaceMapMode,
  marketplaceMapQueryKey,
  type MarketplaceMapResult,
} from 'components/marketplace/browse/marketplaceBrowse';
import type { GeoPoint } from 'components/marketplace/map/types';

import { DEFAULT_LISTING_SORT_SLUG, type ListingSortSlug } from './sortOptions';

const MAX_CACHED_MAP_RESULTS = 8;

export type SearchStatus = 'applied' | 'failed' | 'superseded';

export const useMarketplaceSearch = (
  initialVariables: MarketplaceBrowseVariables,
  initialSort: ListingSortSlug = DEFAULT_LISTING_SORT_SLUG,
  mapMode: MarketplaceMapMode = 'markers',
  mapViewport?: GeoPoint[],
) => {
  const queryClient = useQueryClient();
  const [variables, setVariables] = useState(initialVariables);
  // The sort the on-screen results were actually fetched with. Committed only
  // alongside `variables`, so it never advertises an ordering that a cancelled
  // or failed search merely requested.
  const [appliedSort, setAppliedSort] = useState<ListingSortSlug>(initialSort);
  const [isSearching, setIsSearching] = useState(false);
  const [errorId, setErrorId] = useState<number | null>(null);
  const latestErrorId = useRef(0);
  const latestRequestId = useRef(0);
  const [previewMap, setPreviewMap] = useState<{
    mode: MarketplaceMapMode;
    result: MarketplaceMapResult;
  } | null>(null);
  // The homes that the search finds. The map modes show the same homes in
  // different ways, but the markers mode of the legacy layout leaves out the
  // status filters, so with those filters on it finds other homes.
  const searchedHomes = hashKey([browseSearchVariables(variables, mapMode).listInput]);
  const query = useInfiniteQuery({
    ...marketplaceBrowseQueryOptions({
      variables,
      mapMode,
      mapViewport,
      mapQueryClient: mapMode === 'screen' || mapMode === 'unclustered' ? queryClient : undefined,
    }),
    meta: { listInputKey: searchedHomes },
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.meta?.listInputKey === searchedHomes ? previousData : undefined,
    // The refresh modes pause the observer while a search runs. The markers mode
    // keeps the original behavior, which never fetches the old search again after
    // a failed one.
    enabled: mapMode === 'markers' || !isSearching,
  });

  // Seed the fallback with hydrated data so the initial render needs no state update.
  const [lastSuccessfulResult, setLastSuccessfulResult] = useState<{
    homes: string;
    data: InfiniteData<MarketplaceBrowsePage>;
  } | null>(() => (query.data ? { homes: searchedHomes, data: query.data } : null));
  if (query.data && query.data !== lastSuccessfulResult?.data) {
    setLastSuccessfulResult({ homes: searchedHomes, data: query.data });
  }
  // While another map mode of the same search loads, its last results stay on
  // screen. Results for a different filter set stay hidden until that search finishes.
  const sameHomesResult =
    lastSuccessfulResult?.homes === searchedHomes ? lastSuccessfulResult.data : undefined;
  const pages = (query.data ?? sameHomesResult)?.pages;
  const firstPage = pages?.[0];
  const liveMap = previewMap?.mode === mapMode ? previewMap.result : null;
  const mapMarkers =
    liveMap?.markers ?? (firstPage?.kind === 'initial' ? firstPage.mapMarkers : undefined);
  const mapClusters =
    liveMap?.clusters ?? (firstPage?.kind === 'initial' ? firstPage.mapClusters : undefined);
  const { listings, prefetchAfter } = useMemo(() => {
    if (!pages?.[0]) return { listings: undefined, prefetchAfter: 0 };
    const homes = new Map<string, MarketplaceBrowseListings['listings'][number]>();
    let prefetchAfter = 0;
    pages.forEach((page, index) => {
      if (index === pages.length - 1) prefetchAfter = homes.size;
      page.listings.listings.forEach((listing) => homes.set(listing.addressId, listing));
    });
    return {
      listings: {
        ...pages[0].listings,
        listings: [...homes.values()],
        nextPage: pages.at(-1)?.listings.nextPage ?? null,
      },
      prefetchAfter,
    };
  }, [pages]);
  const initialQueryFailed = query.isError && !query.isFetchNextPageError;
  const { isFetching, hasNextPage, fetchNextPage, refetch } = query;
  const loadMore = useCallback(() => {
    if (isSearching || isFetching) return;
    if (initialQueryFailed) void refetch({ cancelRefetch: false });
    else if (hasNextPage) void fetchNextPage({ cancelRefetch: false });
  }, [isSearching, isFetching, hasNextPage, fetchNextPage, initialQueryFailed, refetch]);

  useEffect(() => {
    if (pages?.length === 1 && !query.isPlaceholderData && !query.isError) loadMore();
  }, [pages?.length, query.isPlaceholderData, query.isError, loadMore]);

  const [dismissedQueryErrorAt, setDismissedQueryErrorAt] = useState(0);
  // Only the refresh modes report the observer's own errors; the markers mode
  // reports the search's error alone, as before.
  const queryErrorId =
    mapMode !== 'markers' &&
    query.isError &&
    !query.isFetchNextPageError &&
    query.errorUpdatedAt > dismissedQueryErrorAt
      ? query.errorUpdatedAt
      : null;
  const activeErrorId = errorId ?? queryErrorId;
  const clearError = useCallback(() => {
    setErrorId(null);
    setDismissedQueryErrorAt(query.errorUpdatedAt);
  }, [query.errorUpdatedAt]);

  useEffect(() => {
    // Viewport keys can retain thousands of markers, so keep only the active browse result.
    if (!isSearching && query.isSuccess && !query.isFetching) {
      queryClient.removeQueries({ queryKey: marketplaceBrowseQueryKey, type: 'inactive' });
    }
    // oxlint-disable-next-line react/exhaustive-effect-dependencies -- react19-baseline: pre-existing, do not copy. Fix the dependency array rather than suppressing
  }, [queryClient, variables, mapMode, isSearching, query.isSuccess, query.isFetching]);

  const runSearch = useCallback(
    async (
      nextVariables: MarketplaceBrowseVariables,
      nextSort?: ListingSortSlug,
      {
        commitAfter,
        mapViewport: searchMapViewport = mapViewport,
      }: {
        /**
         * Holds the fetched results until it resolves: true shows them, false
         * drops them. A search for a camera that is still moving shows its
         * results once the camera lands.
         */
        commitAfter?: Promise<boolean>;
        /** The map polygon for a search whose area the next render only adopts. */
        mapViewport?: GeoPoint[];
      } = {},
    ): Promise<SearchStatus> => {
      const requestId = (latestRequestId.current += 1);
      setIsSearching(true);
      setErrorId(null);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: marketplaceBrowseQueryKey }),
        queryClient.cancelQueries({ queryKey: marketplaceMapQueryKey }),
      ]);
      let acceptsMapResult = true;
      // Early map groups wait while the results may not show yet, and show as
      // soon as they may, even before the list arrives.
      let mapMayShow = commitAfter == null;
      let heldMapResult: MarketplaceMapResult | null = null;
      void commitAfter?.then((landed) => {
        if (!landed) return;
        mapMayShow = true;
        if (heldMapResult && acceptsMapResult && requestId === latestRequestId.current) {
          setPreviewMap({ mode: mapMode, result: heldMapResult });
        }
      });

      try {
        // oxlint-disable-next-line typescript/no-deprecated -- The data layer remains on the current query-client API until its version migration
        await queryClient.fetchInfiniteQuery({
          ...marketplaceBrowseQueryOptions({
            variables: nextVariables,
            mapMode,
            mapViewport: searchMapViewport,
            mapQueryClient: queryClient,
            onMapResult: (result) => {
              if (
                !acceptsMapResult ||
                mapMode === 'markers' ||
                requestId !== latestRequestId.current
              )
                return;
              if (mapMayShow) setPreviewMap({ mode: mapMode, result });
              else heldMapResult = result;
            },
          }),
          pages: 1,
        });
        if (commitAfter && !(await commitAfter)) {
          return 'superseded';
        }
        if (requestId !== latestRequestId.current) {
          return 'superseded';
        }
        setVariables(nextVariables);
        setPreviewMap(null);
        if (nextSort != null) {
          setAppliedSort(nextSort);
        }
        return 'applied';
      } catch (error) {
        if (requestId !== latestRequestId.current || error instanceof CancelledError) {
          return 'superseded';
        }
        Sentry.captureException(error, {
          tags: { feature: 'marketplace-browse', operation: 'search' },
        });
        setErrorId((latestErrorId.current += 1));
        setPreviewMap(null);
        return 'failed';
      } finally {
        acceptsMapResult = false;
        if (requestId === latestRequestId.current) {
          setIsSearching(false);
          const mapQueries = queryClient
            .getQueryCache()
            .findAll({ queryKey: marketplaceMapQueryKey });
          mapQueries
            .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt)
            .slice(MAX_CACHED_MAP_RESULTS)
            .forEach((query) => {
              queryClient.removeQueries({ queryKey: query.queryKey, exact: true });
            });
        }
      }
    },
    [queryClient, mapMode, mapViewport],
  );

  return {
    hasMore: query.hasNextPage,
    prefetchAfter,
    isLoadingMore: query.isFetchingNextPage || (query.isFetching && query.isPlaceholderData),
    loadMoreError: query.isFetchNextPageError || initialQueryFailed,
    loadMore,
    appliedListInput: variables.listInput,
    mapMarkers,
    mapClusters,
    listings,
    appliedSort,
    // Derived from committed `variables` rather than tracked separately, so it
    // can only ever name a page whose results are the ones on screen.
    appliedPage: (variables.listInput.page ?? 0) + 1,
    // Same derivation: the inventory segment of the results on screen, not the
    // one a pending search just requested.
    appliedAvailableSoon: variables.listInput.filters?.availableSoon === true,
    appliedIncludeAvailableSoon: variables.listInput.filters?.includeAvailableSoon === true,
    isLoading: isSearching || query.isPending,
    hasError: activeErrorId !== null,
    errorId: activeErrorId,
    clearError,
    runSearch,
  };
};
